import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import { type ClaimListing, type ClaimQuery, anchorText, listClaims, message } from "./claimsClient";

const LANES = ["validated_conformance", "evidence_based_validation", "ai_forensics"];
const STATES = ["in_progress", "resolved", "held", "conflicted", "unknown", "excluded", "failed"];
const PAGE = 50;

function field(form: FormData, name: string): string {
  return String(form.get(name) ?? "").trim();
}

/** Claims of an engagement or run. Population counts ignore the filters, so they always add up to
 * the whole engagement or run, as run accounting does. */
export function ClaimsPage() {
  const [query, setQuery] = useState<ClaimQuery | null>(null);
  const [listing, setListing] = useState<ClaimListing | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load(next: ClaimQuery) {
    listClaims(next)
      .then((found) => {
        setQuery(next);
        setListing(found);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }

  function onFind(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    load({
      engagement_id: field(form, "engagement"),
      run_id: field(form, "run"),
      lane: field(form, "lane"),
      claim_type: field(form, "type"),
      status: field(form, "status"),
      dimension: field(form, "dimension"),
      dimension_value: field(form, "value"),
      limit: PAGE,
      offset: 0,
    });
  }

  const offset = query?.offset ?? 0;
  return (
    <section aria-labelledby="claims-title">
      <h1 id="claims-title">Claims</h1>
      <form onSubmit={onFind} aria-label="Find claims">
        <label>
          Engagement id <input name="engagement" required />
        </label>
        <label>
          Run id <input name="run" />
        </label>
        <label>
          Lane{" "}
          <select name="lane" defaultValue="">
            <option value="">Any</option>
            {LANES.map((lane) => (
              <option key={lane}>{lane}</option>
            ))}
          </select>
        </label>
        <label>
          Type <input name="type" />
        </label>
        <label>
          Status{" "}
          <select name="status" defaultValue="">
            <option value="">Any</option>
            {STATES.map((state) => (
              <option key={state}>{state}</option>
            ))}
          </select>
        </label>
        <label>
          Boundary dimension <input name="dimension" />
        </label>
        <label>
          Boundary value <input name="value" />
        </label>
        <button type="submit">Show claims</button>
      </form>
      {error && <p role="alert">Could not load: {error}</p>}
      {listing && (
        <>
          <h2>Population</h2>
          <dl aria-label="Population">
            {Object.entries(listing.population).map(([state, count]) => (
              <div key={state}>
                <dt>{state}</dt>
                <dd>{count}</dd>
              </div>
            ))}
          </dl>
          <h2>
            {listing.total} matching {listing.total === 1 ? "claim" : "claims"}
          </h2>
          {listing.claims.length > 0 && (
            <table aria-label="Claims">
              <thead>
                <tr>
                  <th scope="col">Claim</th>
                  <th scope="col">Lane</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Result</th>
                  <th scope="col">Target</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {listing.claims.map((claim) => (
                  <tr key={claim.id}>
                    <td>
                      <Link to={`/claims/${claim.id}`}>{claim.statement}</Link>
                    </td>
                    <td>{claim.lane}</td>
                    <td>{claim.claim_type}</td>
                    <td>{claim.status}</td>
                    <td>{claim.output_kind ? `${claim.output_kind} (${claim.result_form})` : "None yet"}</td>
                    <td>
                      {claim.target_object
                        ? [claim.target_object, claim.target_attribute].filter(Boolean).join(".")
                        : "None"}
                    </td>
                    <td>{claim.anchors[0] ? anchorText(claim.anchors[0]) : "No source"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {query && offset > 0 && (
            <button type="button" onClick={() => load({ ...query, offset: Math.max(offset - PAGE, 0) })}>
              Previous
            </button>
          )}
          {query && offset + PAGE < listing.total && (
            <button type="button" onClick={() => load({ ...query, offset: offset + PAGE })}>
              Next
            </button>
          )}
        </>
      )}
    </section>
  );
}
